import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Store, Plus, Trash2, MapPin, Save, Check, PowerOff, Power, Loader2 } from 'lucide-react';

const API = window.location.origin + '/api';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export default function AdminBranches() {
  const [stores, setStores] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState('');
  const [msg, setMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/supermarkets`, { withCredentials: true })
      .then(r => {
        const list = r.data || [];
        setStores(list);
        if (list.length && !selectedId) setSelectedId(list[0].id);
      })
      .catch(err => console.error('Failed to load stores:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const store = stores.find(s => s.id === selectedId);
    setBranches((store?.branches || []).map(b => ({ ...b })));
    setMsg('');
  }, [selectedId, stores]);

  const patch = (idx, updates) => {
    setBranches(prev => prev.map((b, i) => i === idx ? { ...b, ...updates } : b));
  };

  const addBranch = () => {
    setBranches(prev => [...prev, { id: uid(), name: '', active: true, lat: '', lng: '' }]);
  };

  const removeBranch = (idx) => {
    setBranches(prev => prev.filter((_, i) => i !== idx));
  };

  const detectGps = (idx) => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported by this browser.');
      return;
    }
    setDetecting(String(idx));
    navigator.geolocation.getCurrentPosition(
      pos => {
        patch(idx, {
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        });
        setDetecting('');
      },
      err => {
        console.error(err);
        alert('Could not detect your location. Enter lat/lng manually or try again.');
        setDetecting('');
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const save = async () => {
    setMsg('');
    // Validate: names required, lat/lng if provided must be numeric
    const cleaned = [];
    for (const [i, b] of branches.entries()) {
      if (!b.name.trim()) {
        setMsg(`Branch #${i + 1} needs a name.`);
        return;
      }
      const entry = { id: b.id, name: b.name.trim(), active: b.active !== false };
      if (b.lat !== '' && b.lat !== null && b.lat !== undefined) {
        const n = Number(b.lat);
        if (Number.isNaN(n)) { setMsg(`Branch "${b.name}" has invalid latitude.`); return; }
        entry.lat = n;
      }
      if (b.lng !== '' && b.lng !== null && b.lng !== undefined) {
        const n = Number(b.lng);
        if (Number.isNaN(n)) { setMsg(`Branch "${b.name}" has invalid longitude.`); return; }
        entry.lng = n;
      }
      cleaned.push(entry);
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/supermarkets/${selectedId}/branches`,
        { branches: cleaned },
        { withCredentials: true }
      );
      // reflect any server-assigned ids back into state
      setBranches((data.branches || []).map(b => ({ ...b })));
      setStores(prev => prev.map(s => s.id === selectedId ? { ...s, branches: data.branches } : s));
      setMsg('Saved ✅');
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const store = stores.find(s => s.id === selectedId);

  return (
    <div className="admin-page" data-testid="admin-branches-page" style={{ maxWidth: 1000, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <h2 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Store size={22} /> Branches
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px' }}>
        Manage physical locations for each supermarket. GPS coordinates enable the automatic distance-based delivery fee.
      </p>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}

      {!loading && stores.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', background: 'white', borderRadius: 12, border: '1px dashed var(--border-gray)', color: 'var(--text-muted)' }}>
          No supermarkets yet. Create one first in Admin → Store Settings.
        </div>
      )}

      {!loading && stores.length > 0 && (
        <>
          {/* Supermarket picker */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }} data-testid="store-picker">
            {stores.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                data-testid={`store-pill-${s.id}`}
                style={{
                  padding: '8px 14px', borderRadius: 999, fontSize: '0.82rem', fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: selectedId === s.id ? 'var(--navy)' : '#f1f5f9',
                  color: selectedId === s.id ? 'white' : '#475569',
                }}
              >
                {s.name} {s.branches?.length ? `(${s.branches.length})` : ''}
              </button>
            ))}
          </div>

          {/* Branch cards */}
          {store && (
            <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 14, alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--navy)' }}>{store.name} branches</h3>
                <button
                  onClick={addBranch}
                  data-testid="add-branch-btn"
                  style={{ padding: '7px 12px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <Plus size={13} /> Add branch
                </button>
              </div>

              {branches.length === 0 && (
                <div data-testid="branches-empty" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', background: '#f8fafc', borderRadius: 8 }}>
                  No branches yet. Add one to enable branch pickup on the storefront.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {branches.map((b, i) => (
                  <div
                    key={b.id || i}
                    data-testid={`branch-row-${i}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                      gap: 8, alignItems: 'center',
                      padding: 10, background: b.active === false ? '#F9FAFB' : '#F8FAFC',
                      borderRadius: 8, border: '1px solid var(--border-gray)', opacity: b.active === false ? 0.65 : 1,
                    }}
                  >
                    <input
                      type="text" value={b.name || ''}
                      onChange={e => patch(i, { name: e.target.value })}
                      placeholder="Branch name (e.g. Lusaka - Cairo Rd)"
                      data-testid={`branch-name-${i}`}
                      style={inputStyle}
                    />
                    <input
                      type="text" value={b.lat ?? ''}
                      onChange={e => patch(i, { lat: e.target.value })}
                      placeholder="Latitude"
                      data-testid={`branch-lat-${i}`}
                      style={inputStyle}
                    />
                    <input
                      type="text" value={b.lng ?? ''}
                      onChange={e => patch(i, { lng: e.target.value })}
                      placeholder="Longitude"
                      data-testid={`branch-lng-${i}`}
                      style={inputStyle}
                    />
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button
                        type="button" onClick={() => detectGps(i)} title="Use my current GPS"
                        data-testid={`branch-gps-${i}`}
                        disabled={detecting === String(i)}
                        style={iconBtn('#0369A1', '#E0F2FE')}
                      >
                        {detecting === String(i) ? <Loader2 size={14} className="spin" /> : <MapPin size={14} />}
                      </button>
                      <button
                        type="button" onClick={() => patch(i, { active: b.active === false })} title={b.active === false ? 'Activate' : 'Deactivate'}
                        data-testid={`branch-toggle-${i}`}
                        style={iconBtn(b.active === false ? '#065F46' : '#92400E', b.active === false ? '#D1FAE5' : '#FEF3C7')}
                      >
                        {b.active === false ? <Power size={14} /> : <PowerOff size={14} />}
                      </button>
                      <button
                        type="button" onClick={() => removeBranch(i)} title="Remove"
                        data-testid={`branch-remove-${i}`}
                        style={iconBtn('#991B1B', '#FEE2E2')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16 }}>
                <button
                  onClick={save} disabled={saving}
                  data-testid="save-branches-btn"
                  style={{ padding: '10px 20px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? <Loader2 size={15} className="spin" /> : <Save size={15} />}
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                {msg && (
                  <span data-testid="branches-msg" style={{ fontSize: '0.85rem', color: msg.includes('✅') ? '#065F46' : '#991B1B', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {msg.includes('✅') && <Check size={13} />}
                    {msg}
                  </span>
                )}
              </div>

              <p style={{ marginTop: 14, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                💡 Set GPS for at least one branch to enable automatic distance-based delivery fees. Deactivated branches remain in the database but stop appearing in the customer branch picker.
              </p>
            </div>
          )}
        </>
      )}
      <style>{`.spin{animation:spin 0.8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const inputStyle = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-gray)',
  fontSize: '0.85rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

const iconBtn = (color, bg) => ({
  background: bg, color, border: 'none', borderRadius: 6,
  width: 30, height: 30, display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
});
